import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

export type SupportedCountry = 'CA' | 'IN';

const STORAGE_KEY = 'cwy_country';
const DEFAULT_COUNTRY: SupportedCountry = 'CA';

export const COUNTRY_LABELS: Record<SupportedCountry, { flag: string; name: string }> = {
  CA: { flag: '🇨🇦', name: 'Canada' },
  IN: { flag: '🇮🇳', name: 'India' },
};

export const SUPPORTED_COUNTRIES = Object.keys(COUNTRY_LABELS) as SupportedCountry[];

function readStored(): SupportedCountry {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'CA' || stored === 'IN') return stored;
  } catch {
    // localStorage not available
  }
  return DEFAULT_COUNTRY;
}

interface ClientCountryContextValue {
  country: SupportedCountry;
  setCountry: (next: SupportedCountry) => void;
  flag: string;
  countryName: string;
  COUNTRY_LABELS: typeof COUNTRY_LABELS;
  SUPPORTED_COUNTRIES: SupportedCountry[];
}

const ClientCountryContext = createContext<ClientCountryContextValue | null>(null);

export const ClientCountryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [country, setCountryState] = useState<SupportedCountry>(readStored);

  const setCountry = useCallback((next: SupportedCountry) => {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // ignore
    }
    setCountryState(next);
  }, []);

  const value = useMemo(
    () => ({
      country,
      setCountry,
      flag: COUNTRY_LABELS[country].flag,
      countryName: COUNTRY_LABELS[country].name,
      COUNTRY_LABELS,
      SUPPORTED_COUNTRIES,
    }),
    [country, setCountry]
  );

  return <ClientCountryContext.Provider value={value}>{children}</ClientCountryContext.Provider>;
};

export function useClientCountry(): ClientCountryContextValue {
  const ctx = useContext(ClientCountryContext);
  if (!ctx) {
    throw new Error('useClientCountry must be used within ClientCountryProvider');
  }
  return ctx;
}
